'use strict';

// Web Crypto API (SubtleCrypto) — runs natively in every modern browser with
// no bundler polyfill, and in Node 18+ via the `crypto.webcrypto` global/fallback.
// Matches fintech-gateway's wire contract exactly (see
// fintech-gateway/src/utils/aes.js + checksum.js):
//   - AES-256-GCM, 12-byte random IV, ciphertext/tag reported separately
//   - HMAC-SHA256 checksum over "<timestamp>.<clientId>.<JSON body as sent>"
const subtle = (typeof crypto !== 'undefined' && crypto.subtle)
  ? crypto.subtle
  : require('crypto').webcrypto.subtle;

const TAG_LENGTH_BITS = 128;
const TAG_LENGTH_BYTES = TAG_LENGTH_BITS / 8;

// ── byte/text helpers ────────────────────────────────────────────────

function utf8ToBytes(str) {
  return new TextEncoder().encode(str);
}

function bytesToUtf8(bytes) {
  return new TextDecoder().decode(bytes);
}

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(bytes).toString('base64');
}

function base64ToBytes(b64) {
  if (typeof atob !== 'undefined') {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  return new Uint8Array(Buffer.from(b64, 'base64'));
}

function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Accepts a 32-byte AES key as hex or base64. */
function keyToBytes(keyStr) {
  const isHex = /^[0-9a-fA-F]+$/.test(keyStr) && keyStr.length === 64;
  const bytes = isHex ? new Uint8Array(keyStr.match(/.{2}/g).map((h) => parseInt(h, 16))) : base64ToBytes(keyStr);
  if (bytes.length !== 32) {
    throw new Error(`AES key must decode to 32 bytes, got ${bytes.length}`);
  }
  return bytes;
}

async function importAesKey(keyStr) {
  return subtle.importKey('raw', keyToBytes(keyStr), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

async function importHmacKey(secret) {
  return subtle.importKey('raw', utf8ToBytes(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}

// ── AES-256-GCM ──────────────────────────────────────────────────────

/**
 * Encrypt a JSON-serialisable value. Returns base64 ciphertext, iv and tag
 * as three separate fields — the gateway expects the auth tag reported via
 * X-Tag, not appended to the payload.
 */
async function encrypt(data, keyStr, aad) {
  const key = await importAesKey(keyStr);
  const iv = crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(12)) : require('crypto').webcrypto.getRandomValues(new Uint8Array(12));
  const plaintext = utf8ToBytes(JSON.stringify(data));

  const params = { name: 'AES-GCM', iv, tagLength: TAG_LENGTH_BITS };
  if (aad) params.additionalData = utf8ToBytes(aad);

  const cipherBuf = new Uint8Array(await subtle.encrypt(params, key, plaintext));
  // SubtleCrypto appends the auth tag to the ciphertext — split it back out.
  const ciphertext = cipherBuf.slice(0, cipherBuf.length - TAG_LENGTH_BYTES);
  const tag = cipherBuf.slice(cipherBuf.length - TAG_LENGTH_BYTES);

  return {
    payload: bytesToBase64(ciphertext),
    iv: bytesToBase64(iv),
    tag: bytesToBase64(tag),
  };
}

/** Decrypt a payload produced by `encrypt` (or by the gateway's aes.js). */
async function decrypt(payloadB64, ivB64, tagB64, keyStr, aad) {
  const key = await importAesKey(keyStr);
  const iv = base64ToBytes(ivB64);
  const ciphertext = base64ToBytes(payloadB64);
  const tag = base64ToBytes(tagB64);

  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext, 0);
  combined.set(tag, ciphertext.length);

  const params = { name: 'AES-GCM', iv, tagLength: TAG_LENGTH_BITS };
  if (aad) params.additionalData = utf8ToBytes(aad);

  const plainBuf = await subtle.decrypt(params, key, combined);
  return JSON.parse(bytesToUtf8(new Uint8Array(plainBuf)));
}

// ── HMAC-SHA256 checksum ─────────────────────────────────────────────

/**
 * Canonical string: "<timestamp>.<clientId>.<rawBody>" — `rawBody` must be
 * the exact JSON string sent as the HTTP body (the encrypted envelope,
 * e.g. `{"payload":"..."}`), not the plaintext. See
 * fintech-gateway/src/middleware/verifyChecksum.js.
 */
function buildCanonicalString(timestamp, clientId, rawBody) {
  return `${timestamp}.${clientId}.${rawBody}`;
}

async function checksum(timestamp, clientId, rawBody, secret) {
  const key = await importHmacKey(secret);
  const canonical = buildCanonicalString(timestamp, clientId, rawBody);
  const sig = await subtle.sign('HMAC', key, utf8ToBytes(canonical));
  return bytesToHex(new Uint8Array(sig));
}

module.exports = {
  encrypt,
  decrypt,
  checksum,
  buildCanonicalString,
  // exposed for callers that need raw base64/hex helpers (e.g. tests)
  bytesToBase64,
  base64ToBytes,
  bytesToHex,
};
